import cn from 'clsx'
import React from 'react'
import { useDrop } from 'react-dnd'
import { useDispatch } from 'hooks/use-redux'
import {
  uploadFilesToBrowse,
  uploadFilesToGroups,
} from '@disco/state-management/actions/upload-actions'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { CUSTOM_CHANNEL, TEAM_LIBRARY } from '@disco/constants/GroupTypes'
import { FILE } from '@disco/constants/ItemTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { useGroupLocks } from '@disco/state-management/selectors/groups'
import { getFilesFromDraggedItem } from '@disco/utils/file-utils'
import { DropFilesIndicator } from 'components/ui/drop-indicator'
import type { Group } from 'types/group'

type Props = {
  children: React.ReactNode
  library: Group | null | undefined
}

export default function ExplorerDropWrapper({ children, library }: Props) {
  const dispatch = useDispatch()
  const featureEnabled = useFeatureEnabled(TRACK_NESTING)
  const restrictions = useGroupLocks(library?.id)

  const [{ isOver, canDrop }, dropRef] = useDrop<
    any,
    unknown,
    { isOver: boolean; canDrop: boolean }
  >(
    () => ({
      accept: FILE,
      canDrop: item => {
        if (library?.permissions && !library.permissions.content) {
          return false
        }
        return Boolean(item?.files)
      },
      drop: (draggedItem, monitor) => {
        if (monitor.didDrop()) {
          return
        }
        if (!draggedItem?.items) {
          return
        }
        getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
          if (files.length === 0) {
            return
          }
          if (library?.group_type === TEAM_LIBRARY) {
            dispatch(uploadFilesToBrowse(files))
          } else if (library?.group_type === CUSTOM_CHANNEL && library) {
            dispatch(uploadFilesToGroups(files, [library]))
          }
        })
      },
      collect: monitor => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [dispatch, library],
  )

  if (!featureEnabled || restrictions?.lock_content) {
    return children
  }
  if (
    library?.group_type !== CUSTOM_CHANNEL &&
    library?.group_type !== TEAM_LIBRARY
  ) {
    return children
  }

  return (
    <div
      ref={dropRef}
      className={cn({ HighlightedDropTarget: isOver && canDrop })}
    >
      {children}
      {isOver && canDrop && <DropFilesIndicator />}
    </div>
  )
}
